/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Flex, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { CopyButton } from './copy-button';
import debugMessages from './messages';

const styles = cssMap({
	header: {
		cursor: 'pointer',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
	},
	label: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
	},
	codeBlock: {
		font: token('font.body.small'),
		fontFamily: token('font.family.code'),
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
		maxHeight: '300px',
		overflow: 'auto',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		marginBlockStart: token('space.0'),
		marginBlockEnd: token('space.0'),
		marginInlineStart: token('space.0'),
		marginInlineEnd: token('space.0'),
	},
});

interface CollapsibleCodeBlockProps {
	/** Section title */
	title: string;
	/** Content to display in monospaced block */
	content: string;
	/** Optional: try to pretty-print as JSON */
	isJson?: boolean;
	/** Whether initially expanded */
	defaultExpanded?: boolean;
}

/**
 * A collapsible monospaced text viewer with copy button.
 */
export const CollapsibleCodeBlock = ({
	title,
	content,
	isJson = false,
	defaultExpanded = false,
}: CollapsibleCodeBlockProps): JSX.Element => {
	const [expanded, setExpanded] = useState(defaultExpanded);
	const { formatMessage } = useIntl();

	const toggle = useCallback(() => setExpanded((prev) => !prev), []);

	const safeContent = content ?? '';
	let displayContent = safeContent;
	if (isJson && safeContent) {
		try {
			displayContent = JSON.stringify(JSON.parse(safeContent), null, 2);
		} catch {
			// Use raw content if not valid JSON
		}
	}

	const charCount = safeContent.length;
	const label = formatMessage(debugMessages.charsCount, {
		title,
		count: charCount.toLocaleString(),
	});

	return (
		<Box>
			<Pressable onClick={toggle} xcss={styles.header}>
				<Flex alignItems="center" gap="space.050">
					{expanded ? (
						<ChevronDownIcon
							label={formatMessage(debugMessages.collapseLabel)}
							color={token('color.icon')}
						/>
					) : (
						<ChevronRightIcon
							label={formatMessage(debugMessages.expandLabel)}
							color={token('color.icon')}
						/>
					)}
					<Box as="span" xcss={styles.label}>
						{label}
					</Box>
				</Flex>
			</Pressable>
			{expanded && (
				<Box>
					<CopyButton text={safeContent} label={formatMessage(debugMessages.copyLabel)} />
					<pre css={styles.codeBlock}>
						{displayContent || formatMessage(debugMessages.emptyContent)}
					</pre>
				</Box>
			)}
		</Box>
	);
};
